import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  IconArrowLeft,
  IconCamera,
  IconMessageCircle,
  IconTarget,
  IconDroplet,
  IconBellRinging,
  IconDeviceLaptop,
} from "@tabler/icons-react";
import BackgroundBlobs from "../components/BackgroundBlobs";
import useRevealOnScroll from "../hooks/useRevealOnScroll";
import { handleTiltMove, handleTiltLeave } from "../utils/tilt";

const FEATURES = [
  {
    Icon: IconCamera,
    heading: "Snap a photo, or just type it",
    body: "Send a photo of your meal — or simply describe it — and get calories, protein, carbs, fat, fiber, and sugar estimated automatically.",
  },
  {
    Icon: IconDroplet,
    heading: "Water & activity, logged too",
    body: "Mention a glass of water or a walk in the same message and it's tracked right alongside your meals — no separate flow needed.",
  },
  {
    Icon: IconTarget,
    heading: "Targets built around you",
    body: "Calorie, protein, fiber, and water targets calculated from your own body and goals — not generic guidelines.",
  },
  {
    Icon: IconMessageCircle,
    heading: "Lives right inside Telegram",
    body: "No new app to install — it's a conversation, not a dashboard to open. WhatsApp is next.",
  },
  {
    Icon: IconBellRinging,
    heading: "Reminders that back off",
    body: "Nudges for meals, water, and your weekly weigh-in — automatically skipped the moment you're already logging on your own.",
  },
  {
    Icon: IconDeviceLaptop,
    heading: "Your own dashboard",
    body: "Log in with Telegram to see today's totals, weekly trends, your weight over time, and recent meals — from any browser.",
  },
];

const TrackAndThaw = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useRevealOnScroll();

  return (
    <>
      <section className="project-hero" style={{ "--accent": "#F472B6" }}>
        <BackgroundBlobs colors={["#F472B6", "#FB7185", "#C084FC"]} />
        <div className="section-inner">
          <Link to="/" className="back-link">
            <IconArrowLeft size={16} stroke={2} />
            Back to ThinkCart.ai
          </Link>

          <span className="product-badge project-badge">Private beta</span>

          <h1 className="project-title">Track&amp;Thaw</h1>
          <p className="project-tagline">The nutrition coach that lives in your chat.</p>

          <p className="project-lead">
            Send a photo of your meal — or just describe it — and get calories,
            macros, water, and activity tracked automatically, right inside
            Telegram. No app to download, no food database to search.
          </p>
        </div>
      </section>

      <section className="project-body">
        <div className="section-inner project-copy reveal">
          <p className="founder-body">
            Most nutrition apps ask you to do the work: search a food database, log
            every ingredient, remember to check in. Track&amp;Thaw flips that. Send a
            photo of your meal — or just tell it what you ate — in a chat you
            already use every day, and it does the rest: identifying what's on
            your plate, estimating calories and macros, and tracking your
            progress toward the goals you set at signup.
          </p>
          <p className="founder-body">
            It checks in once a day, nudges you for meals and water without ever
            turning into noise, and adapts to how you actually eat — whether
            that's rice and dal or a protein shake. Log in with your Telegram
            account anytime to see your full history, weekly trends, and weight
            progress on your own dashboard. Built by ThinkCart.ai, currently in
            private beta on Telegram, with WhatsApp coming next.
          </p>
        </div>
      </section>

      <section className="products-section">
        <div className="section-inner">
          <span className="section-label">HOW IT WORKS</span>
          <h2 className="section-title">Everything you need, none of the busywork.</h2>

          <div className="why-grid project-feature-grid">
            {FEATURES.map(({ Icon, heading, body }) => (
              <div
                className="why-col product-card reveal"
                key={heading}
                style={{ "--accent": "#F472B6" }}
                onMouseMove={handleTiltMove}
                onMouseLeave={handleTiltLeave}
              >
                <div className="why-icon project-feature-icon">
                  <Icon size={22} stroke={1.75} />
                </div>
                <h3 className="why-heading">{heading}</h3>
                <p className="why-body project-feature-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="founder-section">
        <div className="founder-inner reveal">
          <span className="section-label">GET INVOLVED</span>
          <h2 className="founder-heading">Interested in the private beta?</h2>
          <p className="founder-body">
            Track&amp;Thaw is currently in private beta on Telegram, with
            WhatsApp coming next. Reach out and we'll let you know when it opens
            up.
          </p>
          <a href="mailto:hello@thinkcart.ai" className="submit-btn project-cta">
            Get in touch →
          </a>
        </div>
      </section>
    </>
  );
};

export default TrackAndThaw;
